import { ProviderEvents, ProviderStatus } from '@openfeature/web-sdk';

import type { FeatureFlagBridge, FeatureFlagEvaluationOptions } from './featureFlagClient.types';
import { getFeatureFlagClient } from './providerRegistry';

export const featureFlagClient: FeatureFlagBridge = {
  getBooleanValue: (key, defaultValue, options) => {
    return getFeatureFlagClient(options?.provider).getBooleanValue(key, defaultValue);
  },
  getBooleanDetails: (key, defaultValue, options) => {
    return getFeatureFlagClient(options?.provider).getBooleanDetails(key, defaultValue);
  },
  getStringValue: (key, defaultValue, options) => {
    return getFeatureFlagClient(options?.provider).getStringValue(key, defaultValue);
  },
  getNumberValue: (key, defaultValue, options) => {
    return getFeatureFlagClient(options?.provider).getNumberValue(key, defaultValue);
  },
  onChange: (callback, options) => {
    const targetClient = getFeatureFlagClient(options?.provider);
    const handler = (): void => {
      callback();
    };

    targetClient.addHandler(ProviderEvents.Ready, handler);
    targetClient.addHandler(ProviderEvents.ConfigurationChanged, handler);
    targetClient.addHandler(ProviderEvents.ContextChanged, handler);

    return (): void => {
      targetClient.removeHandler(ProviderEvents.Ready, handler);
      targetClient.removeHandler(ProviderEvents.ConfigurationChanged, handler);
      targetClient.removeHandler(ProviderEvents.ContextChanged, handler);
    };
  },
  isReady: (options) => {
    return getFeatureFlagClient(options?.provider).providerStatus === ProviderStatus.READY;
  },
};

/**
 * Resolves `true` once the selected provider is READY, or `false` when `timeoutMs`
 * (default 3000) elapses first — callers must treat `false` as "read the safe default".
 * Resolves immediately when the provider is already ready. Portal-internal: the
 * MFE-facing {@link FeatureFlagBridge} contract must not grow beyond the published
 * package API, so this is a module export rather than a bridge method.
 */
export function waitForProviderReady(
  options?: FeatureFlagEvaluationOptions,
  timeoutMs = 3000
): Promise<boolean> {
  if (featureFlagClient.isReady(options)) {
    return Promise.resolve(true);
  }
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      unsubscribe();
      resolve(ready);
    };
    const timer = window.setTimeout(() => finish(false), timeoutMs);
    const unsubscribe = featureFlagClient.onChange(() => {
      if (featureFlagClient.isReady(options)) {
        finish(true);
      }
    }, options);
  });
}
